const clamp = (value, min = 0, max = 100) => Math.max(min, Math.min(max, Number(value) || 0))

const STAT_ALIASES = {
  shotsTotal: ['shots total', 'total shots'],
  shotsOnTarget: ['shots on target', 'shots on goal'],
  shotsOffTarget: ['shots off target'],
  shotsInsideBox: ['shots insidebox', 'shots inside box', 'shots in box', 'shots from inside box', 'shots inside penalty area'],
  shotsOutsideBox: ['shots outsidebox', 'shots outside box', 'shots out box', 'shots from outside box', 'shots outside penalty area'],
  shotsBlocked: ['shots blocked', 'blocked shots'],
  bigChancesCreated: ['big chances created', 'big chance created', 'big chances'],
  corners: ['corners', 'corner kicks'],
  attacks: ['attacks'],
  dangerousAttacks: ['dangerous attacks'],
  accurateCrosses: ['accurate crosses'],
  totalCrosses: ['total crosses', 'crosses'],
  xg: ['xg', 'expected goals'],
  possession: ['ball possession %', 'ball possession', 'possession'],
  passes: ['passes'],
  successfulPasses: ['successful passes', 'accurate passes', 'passes accurate'],
  successfulPassesPercent: ['successful passes percentage', 'accurate passes percentage', 'passes accuracy', 'pass accuracy'],
  longPasses: ['long passes', 'long balls'],
  successfulLongPasses: ['successful long passes', 'long balls won'],
  successfulLongPassesPercent: ['successful long passes percentage', 'long balls won percentage'],
  throwIns: ['throwins', 'throw-ins', 'throw ins'],
  goalKicks: ['goal kicks'],
  finalThirdEntries: ['final third entries', 'passes in final third'],
  fouls: ['fouls'],
  yellowCards: ['yellowcards', 'yellow cards'],
  redCards: ['redcards', 'red cards'],
  substitutions: ['substitutions'],
  goals: ['goals'],
  saves: ['saves'],
  interceptions: ['interceptions'],
  tackles: ['tackles', 'tackles won'],
  duelsWon: ['duels won'],
  clearances: ['clearances'],
}

const LEAGUE_DISPLAY_ALIASES = {
  'pro league': 'Saudi Arabia Pro League',
  'la liga': 'Spain La Liga',
  'serie a': 'Italy Serie A',
  'ligue 1': 'France Ligue 1',
  bundesliga: 'Germany Bundesliga',
  'premier league': 'England Premier League',
}

const STATE_LABELS = {
  home_pressure_building: 'Home Pressure Building',
  away_pressure_building: 'Away Pressure Building',
  balanced_market: 'Balanced Market',
  goal_pressure_rising: 'Goal Pressure Rising',
  high_volatility: 'High Volatility',
  false_pressure: 'False Pressure',
  low_tempo: 'Low Tempo',
  unclear: 'Unclear',
}

const emptySide = () => ({
  shotsTotal: 0,
  shotsOnTarget: 0,
  shotsOffTarget: 0,
  shotsInsideBox: 0,
  shotsOutsideBox: 0,
  shotsBlocked: 0,
  bigChancesCreated: 0,
  corners: 0,
  attacks: 0,
  dangerousAttacks: 0,
  accurateCrosses: 0,
  totalCrosses: 0,
  xg: 0,
  possession: 0,
  passes: 0,
  successfulPasses: 0,
  successfulPassesPercent: 0,
  longPasses: 0,
  successfulLongPasses: 0,
  successfulLongPassesPercent: 0,
  throwIns: 0,
  goalKicks: 0,
  finalThirdEntries: 0,
  fouls: 0,
  yellowCards: 0,
  redCards: 0,
  substitutions: 0,
  goals: 0,
  saves: 0,
  interceptions: 0,
  tackles: 0,
  duelsWon: 0,
  clearances: 0,
})

const normalizeName = (value) => String(value || '').toLowerCase().trim()
const findStatField = (name, aliasesMap) => {
  const entries = Object.entries(aliasesMap)
  const exactMatch = entries.find(([, aliases]) => aliases.some((alias) => normalizeName(alias) === name))
  if (exactMatch) return exactMatch[0]

  return entries
    .flatMap(([field, aliases]) => aliases.map((alias) => ({ field, alias: normalizeName(alias) })))
    .sort((left, right) => right.alias.length - left.alias.length)
    .find(({ alias }) => name.includes(alias))?.field
}

const getSideMeta = (match) => {
  const participants = Array.isArray(match?.participants) ? match.participants : []
  const home = participants.find((participant) => participant?.meta?.location === 'home')
  const away = participants.find((participant) => participant?.meta?.location === 'away')
  return { home, away }
}

const collectStats = (match) => {
  const { home, away } = getSideMeta(match)
  const stats = Array.isArray(match?.statistics) ? match.statistics : []
  const result = {
    home: emptySide(),
    away: emptySide(),
    presentFields: new Set(),
  }

  stats.forEach((stat) => {
    const statSide =
      String(stat?.participant_id) === String(home?.id)
        ? 'home'
        : String(stat?.participant_id) === String(away?.id)
          ? 'away'
          : null

    if (!statSide) return

    const statName = normalizeName(stat?.type?.name || stat?.type_name || stat?.type)
    const value = Number(stat?.data?.value ?? stat?.value ?? 0) || 0

    const field = findStatField(statName, STAT_ALIASES)
    if (!field) return
    result[statSide][field] = value
    result.presentFields.add(field)
  })

  const scores = Array.isArray(match?.scores) ? match.scores : []
  const homeGoals = Number(
    scores.find((score) => String(score?.participant_id) === String(home?.id) && score?.description === 'CURRENT')?.score?.goals || 0
  )
  const awayGoals = Number(
    scores.find((score) => String(score?.participant_id) === String(away?.id) && score?.description === 'CURRENT')?.score?.goals || 0
  )
  result.home.goals = Math.max(result.home.goals, homeGoals)
  result.away.goals = Math.max(result.away.goals, awayGoals)
  result.presentFields.add('goals')

  const events = Array.isArray(match?.events) ? match.events : []
  events.forEach((event) => {
    const eventSide =
      String(event?.participant_id) === String(home?.id)
        ? 'home'
        : String(event?.participant_id) === String(away?.id)
          ? 'away'
          : null

    if (!eventSide) return

    const eventName = normalizeName(event?.type?.name)
    if (eventName.includes('yellow')) result[eventSide].yellowCards += 1
    if (eventName.includes('red')) result[eventSide].redCards += 1
    if (eventName.includes('substitution')) result[eventSide].substitutions += 1
    if (eventName.includes('goal')) result[eventSide].goals += 1
  })

  return result
}

const getCurrentMinute = (match) => {
  const periods = Array.isArray(match?.periods) ? match.periods : []
  const maxPeriodMinute = periods.reduce((highest, period) => Math.max(highest, Number(period?.minutes) || 0), 0)
  if (maxPeriodMinute > 0) return maxPeriodMinute

  const events = Array.isArray(match?.events) ? match.events : []
  const maxEventMinute = events.reduce((highest, event) => Math.max(highest, Number(event?.minute) || 0), 0)
  if (maxEventMinute > 0) return maxEventMinute

  return 0
}

const getScoreContext = (match) => {
  const { home, away } = getSideMeta(match)
  const scores = Array.isArray(match?.scores) ? match.scores : []
  const homeScore =
    Number(
      scores.find((score) => String(score?.participant_id) === String(home?.id) && score?.description === 'CURRENT')?.score?.goals
    ) || 0
  const awayScore =
    Number(
      scores.find((score) => String(score?.participant_id) === String(away?.id) && score?.description === 'CURRENT')?.score?.goals
    ) || 0

  return {
    homeScore,
    awayScore,
    scoreDiff: Math.abs(homeScore - awayScore),
    totalGoals: homeScore + awayScore,
  }
}

const getCoverage = (presentFields) => {
  const required = ['shotsTotal', 'shotsOnTarget', 'corners', 'attacks', 'dangerousAttacks', 'possession', 'passes']
  const missingFields = required.filter((field) => !presentFields.has(field))
  const ratio = (required.length - missingFields.length) / required.length

  if (ratio >= 0.8) return { coverageLevel: 'good', confidence: 0.86, missingFields }
  if (ratio >= 0.5) return { coverageLevel: 'limited', confidence: 0.64, missingFields }
  if (ratio > 0) return { coverageLevel: 'poor', confidence: 0.36, missingFields }
  return { coverageLevel: 'unknown', confidence: 0.15, missingFields }
}

const share = (home, away, fallback = 50) => {
  const total = home + away
  if (!total) return fallback
  return clamp((home / total) * 100)
}

const pressureScore = (side) =>
  Math.min(18, side.shotsTotal) * 3.8 +
  Math.min(10, side.shotsOnTarget) * 12 +
  Math.min(12, side.shotsInsideBox) * 8 +
  Math.min(10, side.shotsBlocked) * 3 +
  Math.min(12, side.corners) * 4.5 +
  Math.min(120, side.attacks) * 0.16 +
  Math.min(80, side.dangerousAttacks) * 1.15 +
  Math.min(20, side.accurateCrosses) * 2.1 +
  Math.min(30, side.totalCrosses) * 0.7 +
  Math.min(5, side.xg) * 16

const estimateExpectedGoals = (side) => {
  const shotsTotal = Math.max(0, Number(side.shotsTotal) || 0)
  let shotsInsideBox = Math.max(0, Number(side.shotsInsideBox) || 0)
  let shotsOutsideBox = Math.max(0, Number(side.shotsOutsideBox) || 0)
  const bigChancesCreated = Math.max(0, Number(side.bigChancesCreated) || 0)

  if (!shotsInsideBox && shotsTotal > 0) {
    shotsInsideBox = Math.max(0, shotsTotal - shotsOutsideBox)
  }
  if (!shotsOutsideBox && shotsInsideBox > 0 && shotsTotal > shotsInsideBox) {
    shotsOutsideBox = Math.max(0, shotsTotal - shotsInsideBox)
  }

  const insideBoxWithoutBigChances = Math.max(0, shotsInsideBox - bigChancesCreated)

  return Number(
    Math.min(
      5,
      bigChancesCreated * 0.38 +
        insideBoxWithoutBigChances * 0.07 +
        shotsOutsideBox * 0.03
    ).toFixed(2)
  )
}

const controlScore = (side) =>
  side.possession * 1.05 +
  Math.min(900, side.passes) * 0.07 +
  Math.min(900, side.successfulPasses) * 0.08 +
  side.successfulPassesPercent * 0.55 +
  Math.min(120, side.longPasses) * 0.08 +
  Math.min(80, side.successfulLongPasses) * 0.14 +
  side.successfulLongPassesPercent * 0.22 +
  Math.min(40, side.throwIns) * 0.3 +
  Math.min(30, side.goalKicks) * 0.14 +
  Math.min(80, side.finalThirdEntries) * 0.22

const resistanceScore = (side) =>
  Math.min(14, side.saves) * 10 +
  Math.min(30, side.interceptions) * 3.5 +
  Math.min(20, side.shotsBlocked) * 4.8 +
  Math.min(40, side.tackles) * 2.3 +
  Math.min(60, side.duelsWon) * 1.15 +
  Math.min(60, side.clearances) * 1.8 +
  Math.min(25, side.goalKicks) * 0.7 +
  Math.min(25, side.fouls) * 0.35

const recentEventSwing = (match, sideLabel) => {
  const minute = getCurrentMinute(match)
  const { home, away } = getSideMeta(match)
  const sideId = sideLabel === 'home' ? home?.id : away?.id
  const events = Array.isArray(match?.events) ? match.events : []

  return events.reduce((score, event) => {
    if (String(event?.participant_id) !== String(sideId)) return score
    const eventMinute = Number(event?.minute) || 0
    if (!minute || eventMinute <= 0) return score
    const age = minute - eventMinute
    if (age < 0 || age > 10) return score

    const recencyWeight = age <= 5 ? 1 : 0.55
    const eventName = normalizeName(event?.type?.name)
    if (eventName.includes('goal')) return score + 18 * recencyWeight
    if (eventName.includes('corner')) return score + 6 * recencyWeight
    if (eventName.includes('shot')) return score + 7 * recencyWeight
    if (eventName.includes('substitution')) return score + 3 * recencyWeight
    return score
  }, 0)
}

const getPatternProfile = (pressureGap, controlGap) => {
  const pressureGapAbs = Math.abs(pressureGap)
  const controlGapAbs = Math.abs(controlGap)
  const sameDirectionalBias =
    pressureGapAbs > 0 &&
    controlGapAbs > 0 &&
    Math.sign(pressureGap) === Math.sign(controlGap)

  if (sameDirectionalBias && pressureGapAbs >= 16 && controlGapAbs >= 10) {
    return pressureGap > 0 ? 'home_pattern' : 'away_pattern'
  }

  if (pressureGapAbs <= 10 && controlGapAbs <= 10) {
    return 'balanced_pattern'
  }

  return 'mixed_pattern'
}

const volatilityLabel = (value) => {
  if (value >= 78) return 'extreme'
  if (value >= 58) return 'high'
  if (value >= 34) return 'medium'
  return 'low'
}

export const calculateLiveIndexesFallback = (match) => {
  const { home, away, presentFields } = collectStats(match)
  if (!presentFields.has('xg')) {
    home.xg = estimateExpectedGoals(home)
    away.xg = estimateExpectedGoals(away)
  }
  const coverage = getCoverage(presentFields)

  const homePressureRaw = pressureScore(home)
  const awayPressureRaw = pressureScore(away)
  const homeControlRaw = controlScore(home)
  const awayControlRaw = controlScore(away)
  const homeResistanceRaw = resistanceScore(home)
  const awayResistanceRaw = resistanceScore(away)

  const pressureHome = Math.round(share(homePressureRaw, awayPressureRaw))
  const pressureAway = 100 - pressureHome
  const controlHome = Math.round(share(homeControlRaw, awayControlRaw))
  const controlAway = 100 - controlHome
  const resistanceHome = Math.round(share(homeResistanceRaw, awayResistanceRaw))
  const resistanceAway = 100 - resistanceHome

  const pressureDelta = pressureHome - pressureAway
  const controlDelta = controlHome - controlAway
  const recentHome = recentEventSwing(match, 'home')
  const recentAway = recentEventSwing(match, 'away')
  const pressureGapAbs = Math.abs(pressureDelta)
  const controlGapAbs = Math.abs(controlDelta)
  const recentEventPulse = recentHome + recentAway
  const patternProfile = getPatternProfile(pressureDelta, controlDelta)

  const totalGoals = home.goals + away.goals
  const totalCards = home.yellowCards + away.yellowCards + (home.redCards + away.redCards) * 2
  const totalSubs = home.substitutions + away.substitutions
  const shotFrequency = home.shotsTotal + away.shotsTotal + home.shotsOnTarget + away.shotsOnTarget
  const alternatingPressure = Math.abs(home.dangerousAttacks - away.dangerousAttacks)
  const structuralImbalance = pressureGapAbs * 0.38 + controlGapAbs * 0.24
  const conflictingSignals = Math.abs(pressureGapAbs - controlGapAbs) * 0.3
  const oneSidedPatternDiscount =
    patternProfile === 'home_pattern' || patternProfile === 'away_pattern'
      ? Math.min(18, pressureGapAbs * 0.16 + controlGapAbs * 0.1)
      : 0
  const volatilityValue = clamp(
    totalGoals * 12 +
      totalCards * 6 +
      totalSubs * 2.5 +
      shotFrequency * 2 +
      (home.fouls + away.fouls) * 1.1 +
      (home.dangerousAttacks + away.dangerousAttacks) * 0.3 +
      alternatingPressure * 0.45 +
      recentEventPulse * 0.35 +
      structuralImbalance +
      conflictingSignals -
      oneSidedPatternDiscount
  )

  return {
    matchId: String(match?.id || ''),
    pressureIndex: {
      home: pressureHome,
      away: pressureAway,
      dominantSide: pressureHome === pressureAway ? 'neutral' : pressureHome > pressureAway ? 'home' : 'away',
    },
    controlIndex: {
      home: controlHome,
      away: controlAway,
      dominantSide: controlHome === controlAway ? 'neutral' : controlHome > controlAway ? 'home' : 'away',
    },
    volatilityIndex: {
      value: Math.round(volatilityValue),
      label: volatilityLabel(volatilityValue),
      profile: patternProfile,
    },
    resistanceIndex: {
      home: resistanceHome,
      away: resistanceAway,
      dominantResistance: resistanceHome === resistanceAway ? 'balanced' : resistanceHome > resistanceAway ? 'home' : 'away',
    },
    coverageLevel: coverage.coverageLevel,
    confidence: coverage.confidence,
    missingFields: coverage.missingFields,
    calculatedAt: new Date().toISOString(),
    sourceSnapshotId: String(match?.id || ''),
    sourceWindow: '10m',
  }
}

export const determineMarketStateFallback = (match, indexes) => {
  if (!indexes || indexes.coverageLevel === 'unknown') {
    return {
      matchId: String(match?.id || ''),
      state: 'unclear',
      label: STATE_LABELS.unclear,
      confidence: 0.18,
      reason: 'Insufficient live data for a reliable market read.',
      blockers: indexes?.missingFields || [],
      calculatedAt: new Date().toISOString(),
    }
  }

  const { home, away } = getSideMeta(match)
  const homeName = home?.name || 'Home'
  const awayName = away?.name || 'Away'
  const homePressure = indexes?.pressureIndex?.home || 0
  const awayPressure = indexes?.pressureIndex?.away || 0
  const homeControl = indexes?.controlIndex?.home || 0
  const awayControl = indexes?.controlIndex?.away || 0
  const homeResistance = indexes?.resistanceIndex?.home || 0
  const awayResistance = indexes?.resistanceIndex?.away || 0
  const volatility = indexes?.volatilityIndex || { value: 0, label: 'low' }
  const { totalGoals } = getScoreContext(match)
  const pressureGap = homePressure - awayPressure
  const controlGap = homeControl - awayControl
  const maxPressure = Math.max(homePressure, awayPressure)
  const maxControl = Math.max(homeControl, awayControl)
  const dominantPressure = homePressure >= awayPressure ? 'home' : 'away'
  const dominantControl = homeControl >= awayControl ? 'home' : 'away'
  const alignedPressureControl = dominantPressure === dominantControl
  const opponentResistance = dominantPressure === 'home' ? awayResistance : homeResistance

  if (indexes.coverageLevel === 'poor') {
    return {
      matchId: String(match?.id || ''),
      state: 'unclear',
      label: STATE_LABELS.unclear,
      confidence: 0.34,
      reason: 'Live feed has too many missing fields to classify the market cleanly.',
      blockers: indexes?.missingFields || [],
      calculatedAt: new Date().toISOString(),
    }
  }

  if (volatility.value >= 75 || volatility.label === 'extreme') {
    return {
      matchId: String(match?.id || ''),
      state: 'high_volatility',
      label: STATE_LABELS.high_volatility,
      confidence: 0.82,
      reason: 'Disruptive events and tempo are making the market unstable.',
      blockers: [],
      calculatedAt: new Date().toISOString(),
    }
  }

  if (maxPressure < 40 && volatility.value < 35 && totalGoals <= 2) {
    return {
      matchId: String(match?.id || ''),
      state: 'low_tempo',
      label: STATE_LABELS.low_tempo,
      confidence: 0.74,
      reason: 'Pressure, event load and recent threat are all low.',
      blockers: [],
      calculatedAt: new Date().toISOString(),
    }
  }

  if (homeControl >= 62 && homePressure <= 45) {
    return {
      matchId: String(match?.id || ''),
      state: 'false_pressure',
      label: STATE_LABELS.false_pressure,
      confidence: 0.68,
      reason: `${homeName} has control, but threat metrics are not following.`,
      blockers: [],
      calculatedAt: new Date().toISOString(),
    }
  }

  if (awayControl >= 62 && awayPressure <= 45) {
    return {
      matchId: String(match?.id || ''),
      state: 'false_pressure',
      label: STATE_LABELS.false_pressure,
      confidence: 0.68,
      reason: `${awayName} has control, but threat metrics are not following.`,
      blockers: [],
      calculatedAt: new Date().toISOString(),
    }
  }

  if (
    maxPressure >= 64 &&
    alignedPressureControl &&
    maxControl >= 54 &&
    opponentResistance <= 48 &&
    volatility.value >= 45 &&
    volatility.value < 75 &&
    totalGoals <= 3
  ) {
    const teamName = dominantPressure === 'home' ? homeName : awayName
    return {
      matchId: String(match?.id || ''),
      state: 'goal_pressure_rising',
      label: STATE_LABELS.goal_pressure_rising,
      confidence: 0.78,
      reason: `${teamName} is combining threat volume with a higher event load.`,
      blockers: [],
      calculatedAt: new Date().toISOString(),
    }
  }

  if (pressureGap >= 14 && controlGap >= 8 && volatility.value < 75) {
    return {
      matchId: String(match?.id || ''),
      state: 'home_pressure_building',
      label: STATE_LABELS.home_pressure_building,
      confidence: 0.76,
      reason: `${homeName} is building pressure with enough control support.`,
      blockers: [],
      calculatedAt: new Date().toISOString(),
    }
  }

  if (pressureGap <= -14 && controlGap <= -8 && volatility.value < 75) {
    return {
      matchId: String(match?.id || ''),
      state: 'away_pressure_building',
      label: STATE_LABELS.away_pressure_building,
      confidence: 0.76,
      reason: `${awayName} is building pressure with enough control support.`,
      blockers: [],
      calculatedAt: new Date().toISOString(),
    }
  }

  if (maxPressure >= 58 && !alignedPressureControl && Math.max(homeResistance, awayResistance) >= 55) {
    const teamName = dominantControl === 'home' ? homeName : awayName
    return {
      matchId: String(match?.id || ''),
      state: 'false_pressure',
      label: STATE_LABELS.false_pressure,
      confidence: 0.66,
      reason: `${teamName} has game control, but the pressure pattern is not clean enough to confirm it.`,
      blockers: [],
      calculatedAt: new Date().toISOString(),
    }
  }

  if (Math.abs(pressureGap) <= 10 && Math.abs(controlGap) <= 10 && volatility.value >= 30 && volatility.value <= 65) {
    return {
      matchId: String(match?.id || ''),
      state: 'balanced_market',
      label: STATE_LABELS.balanced_market,
      confidence: 0.68,
      reason: 'Pressure and control are close enough to call this balanced.',
      blockers: [],
      calculatedAt: new Date().toISOString(),
    }
  }

  return {
    matchId: String(match?.id || ''),
    state: 'unclear',
    label: STATE_LABELS.unclear,
    confidence: 0.48,
    reason: 'The live signals are mixed and do not support a clean label.',
    blockers: [],
    calculatedAt: new Date().toISOString(),
  }
}

export const inferLeagueDisplayName = (league) => {
  const countryName = league?.country?.name || ''
  const leagueName = league?.name || ''
  if (countryName && leagueName) return `${countryName} ${leagueName}`
  const alias = LEAGUE_DISPLAY_ALIASES[normalizeName(leagueName)]
  return alias || leagueName || '--'
}

export const hydrateLiveMatch = (match) => {
  const liveIndexes = match?.liveIndexes || calculateLiveIndexesFallback(match)
  const marketState = match?.marketState || determineMarketStateFallback(match, liveIndexes)

  return {
    ...match,
    lastUpdate: match?.lastUpdate || new Date().toISOString(),
    providerStatus: match?.providerStatus || 'ok',
    source: match?.source || 'live-api',
    isStale: Boolean(match?.isStale),
    liveIndexes,
    marketState,
    league: match?.league
      ? {
          ...match.league,
          displayName: inferLeagueDisplayName(match.league),
        }
      : match?.league,
  }
}

export const sortDashboardMatches = (matches) => {
  const stateRank = (match) => {
    const state = String(match?.state?.state || '').toUpperCase()
    if (['LIVE', 'HT', 'BREAK', 'ET', 'PEN_LIVE', 'INPLAY_1ST_HALF', 'INPLAY_2ND_HALF', 'INPLAY_PENALTIES'].includes(state)) return 0
    if (['NS', 'TBA'].includes(state)) return 1
    return 2
  }

  return [...matches].sort((left, right) => {
    const rankDiff = stateRank(left) - stateRank(right)
    if (rankDiff !== 0) return rankDiff

    const leftStart = Number(left?.starting_at_timestamp) || new Date(left?.starting_at || 0).getTime() || 0
    const rightStart = Number(right?.starting_at_timestamp) || new Date(right?.starting_at || 0).getTime() || 0
    if (leftStart !== rightStart) return leftStart - rightStart

    return String(left?.id || '').localeCompare(String(right?.id || ''))
  })
}
